import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Building2,
    Database,
    RefreshCw,
    Plus,
    Save,
    CheckCircle,
    Loader2,
    UserPlus,
    ChevronRight,
    Settings,
    Server,
    Lock,
    Globe,
    ArrowLeft,
    GitBranch,
    MapPin,
    Shield,
    Pencil
} from 'lucide-react';
import toast from 'react-hot-toast';
import tenantService from '../../services/tenantService';
import SuperAdminLayout from '../../components/SuperAdmin/Layout/SuperAdminLayout';

export default function TenantOnboarding() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('organization');
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    // Form States
    const [orgData, setOrgData] = useState({
        name: '',
        description: '',
        businessType: 'QSR',
        gstNumber: '',
        contact: '',
        email: ''
    });

    const [createdOrg, setCreatedOrg] = useState(null);

    const [branchData, setBranchData] = useState({
        name: '',
        description: '',
        businessType: 'QSR',
        gstNumber: '',
        contact: '',
        email: '',
        organizationId: '',
        modulesSubmodules: ['POS', 'KITCHEN'],
        addresses: [{
            address1: '',
            address2: '',
            city: '',
            state: '',
            zipcode: '',
            country: 'India'
        }]
    });

    const [createdBranch, setCreatedBranch] = useState(null);

    const [dbConfig, setDbConfig] = useState({
        organizationId: '',
        branchId: '',
        dbUrl: 'jdbc:postgresql://localhost:5432/',
        dbUsername: 'postgres',
        dbPassword: '',
        driverClassName: 'org.postgresql.Driver',
        maxPoolSize: 10,
        isActive: true
    });

    const [syncIds, setSyncIds] = useState({
        orgId: '',
        branchId: ''
    });

    const [migrationStatus, setMigrationStatus] = useState(null);

    const [adminData, setAdminData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        branchId: '',
        userType: 'SUPER_USER',
        userRole: 'Owner'
    });

    useEffect(() => {
        if (location.state?.editBranchId) {
            const branchId = location.state.editBranchId;
            setIsEditMode(true);
            setEditLoading(true);

            tenantService.getTenantDetails(branchId)
                .then((data) => {
                    // Prepopulate organization data
                    setOrgData({
                        name: data.organizationName || '',
                        description: data.organizationDescription || '',
                        businessType: data.organizationBusinessType || 'QSR',
                        gstNumber: data.organizationGstNumber || '',
                        contact: data.organizationContact || '',
                        email: data.organizationEmail || ''
                    });
                    setCreatedOrg({ id: data.organizationId, name: data.organizationName });

                    // Prepopulate branch data
                    const addr = data.addresses && data.addresses.length > 0
                        ? data.addresses[0]
                        : { address1: '', address2: '', city: '', state: '', zipcode: '', country: 'India' };
                    setBranchData({
                        name: data.branchName || '',
                        description: data.branchDescription || '',
                        businessType: data.branchBusinessType || 'QSR',
                        gstNumber: data.branchGstNumber || '',
                        contact: data.branchContact || '',
                        email: data.branchEmail || '',
                        organizationId: data.organizationId || '',
                        modulesSubmodules: data.modulesSubmodules || ['POS', 'KITCHEN'],
                        addresses: [{
                            address1: addr.address1 || '',
                            address2: addr.address2 || '',
                            city: addr.city || '',
                            state: addr.state || '',
                            zipcode: addr.zipcode || '',
                            country: addr.country || 'India'
                        }]
                    });
                    setCreatedBranch({ id: data.branchId, name: data.branchName });

                    // Prepopulate DB config
                    setDbConfig({
                        id: data.dbConfigId || '',
                        organizationId: data.organizationId || '',
                        branchId: data.branchId || '',
                        dbUrl: data.dbUrl || 'jdbc:postgresql://localhost:5432/',
                        dbUsername: data.dbUsername || 'postgres',
                        dbPassword: data.dbPassword || '',
                        driverClassName: data.driverClassName || 'org.postgresql.Driver',
                        maxPoolSize: data.maxPoolSize || 10,
                        isActive: data.dbActive !== undefined ? data.dbActive : true
                    });

                    // Prepopulate sync IDs
                    setSyncIds({ orgId: data.organizationId || '', branchId: data.branchId || '' });

                    setAdminData(prev => ({ ...prev, branchId: data.branchId || '' }));

                    setActiveTab('organization');
                    toast.success(`Loaded details for editing`);
                })
                .catch((err) => {
                    toast.error(err.response?.data?.message || 'Failed to load tenant details');
                })
                .finally(() => setEditLoading(false));
        }
    }, [location.state?.editBranchId]);

    // Handle pre-filled data (e.g. from Organization Registry "Add Branch")
    useEffect(() => {
        if (location.state?.organization && !location.state?.editBranchId) {
            const org = location.state.organization;
            setCreatedOrg(org);
            setBranchData(prev => ({
                ...prev,
                organizationId: org.id,
                businessType: org.businessType || 'QSR'
            }));
            setDbConfig(prev => ({ ...prev, organizationId: org.id }));
            setSyncIds(prev => ({ ...prev, orgId: org.id }));
            setActiveTab('branch');
            toast.success(`Starting branch setup for ${org.name}`);
        }
    }, [location.state]);

    const handleCreateOrganization = async () => {
        if (!orgData.name) {
            toast.error("Please enter Organization Name");
            return;
        }

        setLoading(true);
        try {
            if (isEditMode && createdOrg?.id) {
                // Update mode
                const result = await tenantService.updateOrganization(createdOrg.id, orgData);
                setCreatedOrg(result);
                toast.success(`Organization "${result.name}" updated successfully`);
                setActiveTab('branch');
            } else {
                // Create mode
                const result = await tenantService.superAdminCreateOrganization(orgData);
                setCreatedOrg(result);
                setBranchData(prev => ({ ...prev, organizationId: result.id }));
                setDbConfig(prev => ({ ...prev, organizationId: result.id }));
                setSyncIds(prev => ({ ...prev, orgId: result.id }));
                toast.success(`Organization "${result.name}" created successfully`);
                setActiveTab('branch');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to save organization");
        } finally {
            setLoading(false);
        }
    };

    const handleOnboardBranch = async () => {
        if (!branchData.name || !branchData.organizationId) {
            toast.error("Please enter Branch Name and ensure Organization is created");
            return;
        }

        setLoading(true);
        try {
            if (isEditMode && createdBranch?.id) {
                // Update mode
                const result = await tenantService.updateBranch(createdBranch.id, branchData);
                setCreatedBranch(result);
                toast.success(`Branch "${result.name}" updated successfully`);
                setActiveTab('db');
            } else {
                // Create mode
                const result = await tenantService.superAdminCreateBranch(branchData);
                setCreatedBranch(result);
                setDbConfig(prev => ({ ...prev, branchId: result.id }));
                setSyncIds(prev => ({ ...prev, branchId: result.id }));
                setAdminData(prev => ({ ...prev, branchId: result.id }));
                toast.success(`Branch "${result.name}" created successfully`);
                setActiveTab('db');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to save branch");
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterDB = async () => {
        if (!dbConfig.branchId || !dbConfig.dbUrl || !dbConfig.organizationId) {
            toast.error("Please provide Organization ID, Branch ID and Database URL");
            return;
        }

        setLoading(true);
        try {
            if (isEditMode && dbConfig.id) {
                await tenantService.updateDatabase(dbConfig.id, dbConfig);
                toast.success("Tenant database updated successfully");
            } else {
                await tenantService.superAdminRegisterDatabase(dbConfig);
                toast.success("Tenant database registered successfully");
            }
            setActiveTab('migrations');
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to register database");
        } finally {
            setLoading(false);
        }
    };

    const handleRunMigrations = async () => {
        if (!syncIds.orgId || !syncIds.branchId) {
            toast.error("Missing Organization or Branch ID for migration");
            return;
        }

        setLoading(true);
        try {
            await tenantService.superAdminRunTenantMigration(syncIds.orgId, syncIds.branchId);
            toast.success("Database migrations completed successfully");
            setActiveTab('sync');
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Migration failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSyncData = async () => {
        if (!syncIds.orgId || !syncIds.branchId) {
            toast.error("Organization or Branch ID missing");
            return;
        }

        setLoading(true);
        try {
            await tenantService.superAdminSyncOrganization(syncIds.orgId, syncIds.branchId);
            toast.success("Data synced to tenant database successfully");
            setActiveTab('adminUser');
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to sync data");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAdminUser = async () => {
        if (!adminData.username || !adminData.password || !adminData.email) {
            toast.error("Username, Password and Email are required");
            return;
        }

        setLoading(true);
        try {
            await tenantService.superAdminCreateAdminUser(syncIds.orgId, adminData);
            toast.success("Onboarding Complete! Admin user created.");
            resetAllForms();
            setShowSuccessModal(true);
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to create admin user");
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        navigate('/platform/organizations');
    };

    const checkMigrationStatus = async () => {
        setStatusLoading(true);
        try {
            const status = await tenantService.superAdminGetMigrationStatus();
            setMigrationStatus(status);
            toast.success("Status updated");
        } catch (error) {
            toast.error("Failed to fetch status");
        } finally {
            setStatusLoading(false);
        }
    };

    const resetAllForms = () => {
        setOrgData({
            name: '',
            description: '',
            businessType: 'QSR',
            gstNumber: '',
            contact: '',
            email: ''
        });
        setCreatedOrg(null);
        setBranchData({
            name: '',
            description: '',
            businessType: 'QSR',
            gstNumber: '',
            contact: '',
            email: '',
            organizationId: '',
            modulesSubmodules: ['POS', 'KITCHEN'],
            addresses: [{
                address1: '',
                address2: '',
                city: '',
                state: '',
                zipcode: '',
                country: 'India'
            }]
        });
        setCreatedBranch(null);
        setDbConfig({
            organizationId: '',
            branchId: '',
            dbUrl: 'jdbc:postgresql://localhost:5432/',
            dbUsername: 'postgres',
            dbPassword: '',
            driverClassName: 'org.postgresql.Driver',
            maxPoolSize: 10,
            isActive: true
        });
        setSyncIds({
            orgId: '',
            branchId: ''
        });
        setAdminData({
            firstName: '',
            lastName: '',
            email: '',
            username: '',
            password: '',
            branchId: ''
        });
        setMigrationStatus(null);
    };

    // Stepper Steps
    const steps = [
        { id: 'organization', label: 'Organization', icon: Building2, step: 1 },
        { id: 'branch', label: 'Branch', icon: GitBranch, step: 2 },
        { id: 'db', label: 'Database', icon: Database, step: 3 },
        { id: 'migrations', label: 'Migrations', icon: Settings, step: 4 },
        { id: 'sync', label: 'Sync', icon: RefreshCw, step: 5 },
        { id: 'adminUser', label: 'Admin User', icon: UserPlus, step: 6 }
    ];

    const getCurrentStepIndex = () => {
        return steps.findIndex(s => s.id === activeTab);
    };

    return (
        <SuperAdminLayout>
            <div className="flex flex-col h-full px-6 py-6">
                {/* Edit Loading Overlay */}
                {editLoading && (
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl p-8 shadow-2xl flex flex-col items-center gap-4">
                            <Loader2 size={40} className="animate-spin text-indigo-600" />
                            <p className="text-slate-700 font-semibold">Loading tenant details...</p>
                        </div>
                    </div>
                )}

                {/* HEADER */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        {isEditMode && <Pencil size={22} className="text-amber-500" />}
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">
                                {isEditMode ? 'Edit Tenant' : 'Tenant Onboarding'}
                            </h1>
                            <p className="text-sm text-slate-500">
                                {isEditMode ? 'Update organization or branch details' : 'Set up new organization or branch'}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/platform/organizations')}
                        className="px-4 py-2 rounded-lg bg-white border text-slate-600 hover:bg-slate-50 flex items-center gap-2 text-sm font-medium"
                    >
                        <ArrowLeft size={16} />
                        Back to Registry
                    </button>
                </div>

                {/* Stepper Progress */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
                    <div className="flex items-center justify-between overflow-x-auto">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            const isActive = activeTab === step.id;
                            const isPast = getCurrentStepIndex() > index;

                            return (
                                <React.Fragment key={step.id}>
                                    <button
                                        onClick={() => {
                                            if (isEditMode || isPast) setActiveTab(step.id);
                                        }}
                                        disabled={!isEditMode && !isPast && !isActive}
                                        className={`flex flex-col items-center gap-2 px-4 py-2 rounded-lg transition-all min-w-[100px] ${isActive
                                                ? 'bg-indigo-50'
                                                : (isPast || isEditMode) ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                    >
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${isActive
                                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                                                : isPast
                                                    ? 'bg-green-100 text-green-600'
                                                    : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {isPast ? <CheckCircle size={20} /> : <Icon size={20} />}
                                        </div>
                                        <span className={`text-xs font-semibold ${isActive ? 'text-indigo-600' : isPast ? 'text-green-600' : 'text-gray-400'
                                            }`}>
                                            {step.label}
                                        </span>
                                    </button>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-2 min-w-[20px] ${isPast ? 'bg-green-300' : 'bg-gray-200'
                                            }`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-auto pb-8">
                    {/* Step 1: Create Organization */}
                    {activeTab === 'organization' && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                                            <Building2 size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900">Organization Setup</h3>
                                            <p className="text-sm text-slate-500">Define the core tenant entity</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Legal Name *</label>
                                            <input
                                                type="text"
                                                value={orgData.name}
                                                onChange={e => setOrgData({ ...orgData, name: e.target.value })}
                                                placeholder="e.g. Maverick Dine Restaurants"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Description</label>
                                            <textarea
                                                value={orgData.description}
                                                onChange={e => setOrgData({ ...orgData, description: e.target.value })}
                                                rows="2"
                                                placeholder="Brief description..."
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white outline-none transition-all resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Business Type</label>
                                            <select
                                                value={orgData.businessType}
                                                onChange={e => setOrgData({ ...orgData, businessType: e.target.value })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                                            >
                                                <option value="QSR">QSR (Quick Service)</option>
                                                <option value="FINE_DINE">Fine Dine</option>
                                                <option value="CAFE">Cafe</option>
                                                <option value="BAKERY">Bakery</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">GST Number</label>
                                            <input
                                                type="text"
                                                value={orgData.gstNumber}
                                                onChange={e => setOrgData({ ...orgData, gstNumber: e.target.value.toUpperCase() })}
                                                placeholder="e.g. 29AABCX..."
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Contact</label>
                                            <input
                                                type="text"
                                                value={orgData.contact}
                                                onChange={e => setOrgData({ ...orgData, contact: e.target.value })}
                                                placeholder="+91..."
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Email</label>
                                            <input
                                                type="email"
                                                value={orgData.email}
                                                onChange={e => setOrgData({ ...orgData, email: e.target.value })}
                                                placeholder="org@example.com"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                    </div>

                                    {createdOrg && (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-green-700 font-semibold mb-1">
                                                <CheckCircle size={18} />
                                                Organization Created
                                            </div>
                                            <div className="text-sm text-green-600">
                                                <span className="font-bold">{createdOrg.name}</span>
                                                <span className="ml-2 text-xs bg-green-100 px-2 py-0.5 rounded font-mono">{createdOrg.id}</span>
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleCreateOrganization}
                                        disabled={loading || (!isEditMode && createdOrg)}
                                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 size={20} className="animate-spin" /> : isEditMode ? <Save size={20} /> : <Plus size={20} />}
                                        {isEditMode
                                            ? 'Update Organization & Proceed'
                                            : createdOrg ? 'Success - Continue to Branch' : 'Create Organization & Proceed'
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Onboard Branch */}
                    {activeTab === 'branch' && (
                        <div className="max-w-3xl mx-auto">
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                                            <GitBranch size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-slate-900">Branch Configuration</h3>
                                            <p className="text-sm text-slate-500">Configure operational location for {createdOrg?.name || 'organization'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Branch Name *</label>
                                            <input
                                                type="text"
                                                value={branchData.name}
                                                onChange={e => setBranchData({ ...branchData, name: e.target.value })}
                                                placeholder="e.g. Downtown Flagship"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Operational Model</label>
                                            <select
                                                value={branchData.businessType}
                                                onChange={e => setBranchData({ ...branchData, businessType: e.target.value })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                                            >
                                                <option value="QSR">QSR (Quick Service)</option>
                                                <option value="FINE_DINE">Fine Dine</option>
                                                <option value="CAFE">Cafe</option>
                                                <option value="BAKERY">Bakery</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Branch GSTIN</label>
                                            <input
                                                type="text"
                                                value={branchData.gstNumber}
                                                onChange={e => setBranchData({ ...branchData, gstNumber: e.target.value.toUpperCase() })}
                                                placeholder="e.g. 29AABCX..."
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Business Contact</label>
                                            <input
                                                type="text"
                                                value={branchData.contact}
                                                onChange={e => setBranchData({ ...branchData, contact: e.target.value })}
                                                placeholder="+91 999 999 9999"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Public Email</label>
                                            <input
                                                type="email"
                                                value={branchData.email}
                                                onChange={e => setBranchData({ ...branchData, email: e.target.value })}
                                                placeholder="branch@maverick.com"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-3">Enabled Modules</label>
                                        <div className="flex gap-2 flex-wrap">
                                            {['POS', 'KITCHEN', 'INVENTORY', 'REPORTS'].map(module => (
                                                <button
                                                    key={module}
                                                    type="button"
                                                    onClick={() => {
                                                        const current = branchData.modulesSubmodules;
                                                        const updated = current.includes(module)
                                                            ? current.filter(m => m !== module)
                                                            : [...current, module];
                                                        setBranchData({ ...branchData, modulesSubmodules: updated });
                                                    }}
                                                    className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all border ${branchData.modulesSubmodules.includes(module)
                                                        ? 'bg-purple-600 border-purple-600 text-white shadow-lg shadow-purple-200'
                                                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                                                        }`}
                                                >
                                                    {module}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-gray-100">
                                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
                                            <MapPin size={14} className="text-red-400" />
                                            Physical Address
                                        </label>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="col-span-2">
                                                <input
                                                    type="text"
                                                    value={branchData.addresses[0].address1}
                                                    onChange={e => {
                                                        const newAddresses = [...branchData.addresses];
                                                        newAddresses[0].address1 = e.target.value;
                                                        setBranchData({ ...branchData, addresses: newAddresses });
                                                    }}
                                                    placeholder="Street Address"
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                value={branchData.addresses[0].city}
                                                onChange={e => {
                                                    const newAddresses = [...branchData.addresses];
                                                    newAddresses[0].city = e.target.value;
                                                    setBranchData({ ...branchData, addresses: newAddresses });
                                                }}
                                                placeholder="City"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                            />
                                            <input
                                                type="text"
                                                value={branchData.addresses[0].state}
                                                onChange={e => {
                                                    const newAddresses = [...branchData.addresses];
                                                    newAddresses[0].state = e.target.value;
                                                    setBranchData({ ...branchData, addresses: newAddresses });
                                                }}
                                                placeholder="State"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                    </div>

                                    {createdBranch && (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-green-700 font-semibold mb-1">
                                                <CheckCircle size={18} />
                                                Branch Created
                                            </div>
                                            <div className="text-sm text-green-600">
                                                <span className="font-bold">{createdBranch.name}</span>
                                                <span className="ml-2 text-xs bg-green-100 px-2 py-0.5 rounded font-mono">{createdBranch.id}</span>
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleOnboardBranch}
                                        disabled={loading || !branchData.organizationId || (!isEditMode && createdBranch)}
                                        className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-200 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 size={20} className="animate-spin" /> : isEditMode ? <Save size={20} /> : <ChevronRight size={20} />}
                                        {isEditMode 
                                            ? 'Update Branch & Proceed' 
                                            : createdBranch ? 'Success - Continue to Database' : 'Create Branch & Proceed'
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Database Config */}
                    {activeTab === 'db' && (
                        <div className="max-w-3xl mx-auto">
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                                            <Server size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900">Database Configuration</h3>
                                            <p className="text-sm text-slate-500">Configure dedicated database for this branch</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    {/* Context Info */}
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Organization</label>
                                            <div className="flex items-center gap-2">
                                                <div className="w-1.5 h-6 bg-indigo-400 rounded-full" />
                                                <span className="font-bold text-slate-800">{createdOrg?.name || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Branch</label>
                                            <div className="flex items-center gap-2">
                                                <div className="w-1.5 h-6 bg-purple-400 rounded-full" />
                                                <span className="font-bold text-slate-800">{createdBranch?.name || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">JDBC Connection URL *</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={dbConfig.dbUrl}
                                                onChange={e => setDbConfig({ ...dbConfig, dbUrl: e.target.value })}
                                                className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-blue-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white outline-none transition-all"
                                                placeholder="jdbc:postgresql://host:port/dbname"
                                            />
                                            <Globe size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Database Username</label>
                                            <input
                                                type="text"
                                                value={dbConfig.dbUsername}
                                                onChange={e => setDbConfig({ ...dbConfig, dbUsername: e.target.value })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Database Password</label>
                                            <div className="relative">
                                                <input
                                                    type="password"
                                                    value={dbConfig.dbPassword}
                                                    onChange={e => setDbConfig({ ...dbConfig, dbPassword: e.target.value })}
                                                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white outline-none transition-all"
                                                />
                                                <Lock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleRegisterDB}
                                        disabled={loading || !dbConfig.branchId}
                                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                                        {isEditMode ? 'Update Database & Proceed' : 'Register Database & Proceed'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Migrations */}
                    {activeTab === 'migrations' && (
                        <div className="max-w-3xl mx-auto">
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-teal-50 to-green-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-teal-600">
                                            <Settings size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900">Database Migrations</h3>
                                            <p className="text-sm text-slate-500">Initialize business tables in tenant database</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Organization</label>
                                            <div className="text-sm font-bold text-teal-600">{createdOrg?.name || 'N/A'}</div>
                                            <div className="mt-1 text-xs font-mono text-gray-400">{syncIds.orgId || 'N/A'}</div>
                                        </div>
                                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Branch</label>
                                            <div className="text-sm font-bold text-teal-600">{createdBranch?.name || 'N/A'}</div>
                                            <div className="mt-1 text-xs font-mono text-gray-400">{syncIds.branchId || 'N/A'}</div>
                                        </div>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={handleRunMigrations}
                                            disabled={loading || !syncIds.branchId}
                                            className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-teal-200 disabled:opacity-50"
                                        >
                                            {loading ? <Loader2 size={20} className="animate-spin" /> : <Settings size={20} />}
                                            Run Migrations & Proceed
                                        </button>
                                        <button
                                            type="button"
                                            onClick={checkMigrationStatus}
                                            disabled={statusLoading}
                                            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold flex items-center gap-2 transition-all"
                                        >
                                            {statusLoading && <Loader2 size={18} className="animate-spin" />}
                                            Check Status
                                        </button>
                                    </div>

                                    {migrationStatus && (
                                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-semibold text-slate-700">Migration Status</h4>
                                                <span className="text-xs bg-gray-200 px-2 py-1 rounded text-gray-600 font-medium">
                                                    Total: {migrationStatus.totalActiveTenants || 0}
                                                </span>
                                            </div>
                                            <pre className="text-sm text-gray-600 overflow-auto max-h-40">
                                                {JSON.stringify(migrationStatus, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Sync Data */}
                    {activeTab === 'sync' && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600">
                                            <RefreshCw size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900">Data Synchronization</h3>
                                            <p className="text-sm text-slate-500">Sync organization and branch data to tenant database</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <p className="text-xs font-semibold text-gray-400 uppercase mb-3 text-center">Sync Parameters</p>
                                        <div className="flex items-center justify-center gap-8">
                                            <div className="text-center">
                                                <label className="text-xs font-bold text-gray-400 block mb-1">ORGANIZATION</label>
                                                <span className="text-sm font-bold text-slate-800">{createdOrg?.name || 'N/A'}</span>
                                                <div className="mt-1 text-xs font-mono text-gray-400">{syncIds.orgId}</div>
                                            </div>
                                            <div className="w-px h-10 bg-gray-200" />
                                            <div className="text-center">
                                                <label className="text-xs font-bold text-gray-400 block mb-1">BRANCH</label>
                                                <span className="text-sm font-bold text-slate-800">{createdBranch?.name || 'N/A'}</span>
                                                <div className="mt-1 text-xs font-mono text-gray-400">{syncIds.branchId}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleSyncData}
                                        disabled={loading || !syncIds.orgId || !syncIds.branchId}
                                        className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-amber-200 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
                                        Sync Data & Proceed
                                    </button>

                                    <p className="text-xs text-gray-400 text-center">
                                        This mirrors Master records into the active Tenant database.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 6: Create Super User */}
                    {activeTab === 'adminUser' && (
                        <div className="max-w-3xl mx-auto">
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center text-violet-600">
                                            <UserPlus size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900">Root Administrator</h3>
                                            <p className="text-sm text-slate-500">Initialize the first system admin for this branch</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    {/* Context Info */}
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Target Entity</label>
                                            <span className="font-bold text-slate-800 text-sm">{createdOrg?.name || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Target Branch</label>
                                            <span className="font-bold text-slate-800 text-sm">{createdBranch?.name || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">First Name</label>
                                            <input
                                                type="text"
                                                value={adminData.firstName}
                                                onChange={e => setAdminData({ ...adminData, firstName: e.target.value })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white outline-none transition-all"
                                                placeholder="John"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Last Name</label>
                                            <input
                                                type="text"
                                                value={adminData.lastName}
                                                onChange={e => setAdminData({ ...adminData, lastName: e.target.value })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white outline-none transition-all"
                                                placeholder="Doe"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Email Address *</label>
                                            <input
                                                type="email"
                                                value={adminData.email}
                                                onChange={e => setAdminData({ ...adminData, email: e.target.value })}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white outline-none transition-all"
                                                placeholder="admin@restaurant.com"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">System Username *</label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={adminData.username}
                                                    onChange={e => setAdminData({ ...adminData, username: e.target.value })}
                                                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white outline-none transition-all"
                                                    placeholder="admin_root"
                                                />
                                                <Shield size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Access Passcode *</label>
                                            <div className="relative">
                                                <input
                                                    type="password"
                                                    value={adminData.password}
                                                    onChange={e => setAdminData({ ...adminData, password: e.target.value })}
                                                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:bg-white outline-none transition-all"
                                                    placeholder="••••••••"
                                                />
                                                <Lock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleCreateAdminUser}
                                        disabled={loading}
                                        className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-200 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
                                        Create Admin User & Complete Setup
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Success Modal */}
                {showSuccessModal && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center">
                            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle size={40} className="text-green-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-3">Onboarding Complete!</h3>
                            <p className="text-slate-500 mb-8">
                                <span className="font-bold text-green-700">{orgData.name}</span> has been successfully onboarded with a dedicated tenant database and super user access.
                            </p>
                            <button
                                onClick={handleModalClose}
                                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-all"
                            >
                                Return to Registry
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
}
